import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AboutUs.css';
import { useNavigate } from "react-router-dom";
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import logo from './assets/logo.png'; // Import your logo
import Modal from './modal';
// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },
  isLoggedIn: () => localStorage.getItem('isLoggedIn') === 'true',
  getLoginData: () => ({
    userId: localStorage.getItem('userId'),
    email: localStorage.getItem('userEmail'),
    name: localStorage.getItem('userName'),
    userType: localStorage.getItem('userType'),
  }),
  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

const AboutUs = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [logged_in, setLogged_in] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const profileWidgetRef = useRef(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookie, setShowCookie] = useState(false);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);

  const handleMobileNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Sticky top bar state (presentational only).
  // Replaces the previous isMobile/resize-listener branch — the desktop
  // vs mobile nav split is now handled entirely by CSS media queries,
  // so there is no re-render on resize and no body.overflow mutation.
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Firebase Authentication Check (keeping your existing code)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(true);
      if (user) {
        try {
          const loginData = LocalStorageHelper.getLoginData();
          let userType = loginData.userType || 'user';
          let userDocRef = doc(db, userType === 'vendor' || userType === 'photographer' ? 'photographers' : 'users', user.uid);
          let userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            userType = userType === 'vendor' || userType === 'photographer' ? 'user' : 'photographer';
            userDocRef = doc(db, userType === 'photographer' ? 'photographers' : 'users', user.uid);
            userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              LocalStorageHelper.saveLoginData(
                user.uid,
                user.email,
                user.displayName || "User",
                userType
              );
            }
          }

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const getAvatarUrl = (userData, user) => {
              const avatarSources = [
                userData.profileImage,
                userData.avatarUrl,
                userData.avatar,
                user.photoURL
              ];
              const validAvatar = avatarSources.find(url => url && url.trim() !== '' && url !== 'undefined');
              if (validAvatar) return validAvatar;
              const name = userData.name || user.displayName || "User";
              return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
            };
            const profileData = {
              id: user.uid,
              name: userData.name || user.displayName || "User",
              email: userData.email || user.email,
              avatar: getAvatarUrl(userData, user),
              role: userType,
              joinDate: userData.createdAt,
              isVerified: user.emailVerified || userData.emailVerified || false,
              userType: userType
            };
            setUserProfile(profileData);
            setLogged_in(true);
            LocalStorageHelper.saveLoginData(
              user.uid,
              userData.email || user.email,
              userData.name || user.displayName || "User",
              userType
            );
          } else {
            const basicProfile = {
              id: user.uid,
              name: user.displayName || "User",
              email: user.email,
              userType: 'user',
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
              isVerified: user.emailVerified || false
            };
            setUserProfile(basicProfile);
            setLogged_in(true);
            LocalStorageHelper.saveLoginData(
              user.uid,
              user.email,
              user.displayName || "User",
              'user'
            );
          }
        } catch (error) {
          setUserProfile({
            id: user.uid,
            name: user.displayName || "User",
            email: user.email,
            userType: 'user',
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
            isVerified: user.emailVerified || false
          });
          setLogged_in(true);
        }
      } else {
        setLogged_in(false);
        setUserProfile(null);
        LocalStorageHelper.clearLoginData();
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      LocalStorageHelper.clearLoginData();
      setLogged_in(false);
      setUserProfile(null);
      setShowProfileMenu(false);
      setIsMenuOpen(false);
    } catch (error) {}
  }, []);

  const toggleProfileMenu = () => setShowProfileMenu(prev => !prev);

  const handleProfileClick = () => {
    setShowProfileMenu(false);
    setIsMenuOpen(false);
    
    if (userProfile?.userType === 'vendor' || userProfile?.userType === 'photographer') {
      navigate('/Dashboard');
    } else {
      navigate('/ProfileSettings');
    }
  };

  const handleSettingsClick = () => {
    setShowProfileMenu(false);
    setIsMenuOpen(false);
    navigate('/ProfileSettings');
  };

  const handleMyBookingsClick = () => {
    setShowProfileMenu(false);
    setIsMenuOpen(false);
    navigate('/UserBookings');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileWidgetRef.current && !profileWidgetRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  // Escape closes the mobile nav sheet
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isMenuOpen]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    setEmail('');
  };

  const renderNavbarAuthButton = () => {
    if (authLoading) {
      return (
        <div className="About-landing-auth-loading">
          <div className="About-landing-loading-spinner"></div>
        </div>
      );
    }
    if (!logged_in) {
      return (
        <button type="button" className="About-landing-login-btn" onClick={() => navigate("/login")}>
          Log In
        </button>
      );
    }
    return <div></div>;
  };

  const renderProfileWidget = () => {
    if (!logged_in || !userProfile || authLoading) return null;
    
    const isPhotographer = userProfile.userType === 'vendor' || userProfile.userType === 'photographer';
    
    return (
      <div className="About-floating-profile-widget" ref={profileWidgetRef}>
        <div className="About-profile-widget-trigger" onClick={toggleProfileMenu}>
          <img 
            src={userProfile.avatar} 
            alt={userProfile.name} 
            className="About-profile-widget-avatar"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
            }}
          />
          <div className="About-profile-widget-info">
            <span className="About-profile-widget-name">{userProfile.name}</span>
            <span className="About-profile-widget-role">
              {isPhotographer ? 'Photographer' : 'User'}
            </span>
          </div>
          <div className="About-profile-widget-chevron">
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              style={{ transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </div>
        </div>
        {showProfileMenu && (
          <div className="About-profile-widget-menu">
            <div className="About-profile-widget-menu-header">
              <img 
                src={userProfile.avatar} 
                alt={userProfile.name} 
                className="About-profile-widget-menu-avatar"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
                }}
              />
              <div className="About-profile-widget-menu-info">
                <h4>{userProfile.name}</h4>
                <p>{userProfile.email}</p>
                <span className="About-profile-widget-menu-badge">
                  {isPhotographer ? 'Photographer' : 'User'}
                </span>
              </div>
            </div>
            <div className="About-profile-widget-menu-divider"></div>
            <div 
              className="About-profile-widget-menu-item About-profile-widget-menu-item-primary"
              onClick={handleProfileClick}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isPhotographer ? (
                  <>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  </>
                ) : (
                  <>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </>
                )}
              </svg>
              <span>{isPhotographer ? ' Dashboard' : ' Profile'}</span>
            </div>
            
            {!isPhotographer && (
              <div 
                className="About-profile-widget-menu-item"
                onClick={handleMyBookingsClick}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span> My Bookings</span>
              </div>
            )}
            
            {!isPhotographer && (
              <div 
                className="About-profile-widget-menu-item"
                onClick={handleSettingsClick}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                </svg>
                <span> Settings</span>
              </div>
            )}
            
            <div className="About-profile-widget-menu-divider"></div>
            <div 
              className="About-profile-widget-menu-item About-profile-widget-menu-item-logout"
              onClick={handleLogout}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16,17 21,12 16,7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span> Log Out</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="About-about-container">
      {renderProfileWidget()}

      {/* Single top bar — desktop links and the mobile sheet are the same
          markup, switched by CSS media query rather than a JS branch. */}
      <header className={`About-topbar ${isScrolled ? 'is-stuck' : ''}`}>
        <div className="About-top-left-logo" onClick={() => navigate("/")}>
          <img src={logo} alt="LitStudio" className="About-top-logo-image" />
        </div>

        <nav className="About-landing-navbar">
          <div className="About-landing-nav-container">
            <div className="About-landing-nav-pill">
              <div className="About-landing-nav-links-container">
                <button type="button" className="About-landing-nav-link" onClick={() => navigate("/")}>
                  Home
                </button>
                <button type="button" className="About-landing-nav-link" onClick={() => navigate("/BrowsePhotographer")}>
                  Browse Photographers
                </button>
                <button type="button" className="About-landing-nav-link" onClick={() => navigate("/Login")}>
                  Join as Photographer
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="About-nav-actions">
          {renderNavbarAuthButton()}
          <button
            type="button"
            className={`About-hamburger-btn ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span className="About-hamburger-line"></span>
            <span className="About-hamburger-line"></span>
            <span className="About-hamburger-line"></span>
          </button>
        </div>
      </header>

      <div className={`About-mobile-menu ${isMenuOpen ? 'active' : ''}`}>
        <button type="button" className="About-mobile-nav-link" onClick={() => handleMobileNavClick('/')}>
          Home
        </button>
        <button type="button" className="About-mobile-nav-link" onClick={() => handleMobileNavClick('/BrowsePhotographer')}>
          Browse Photographers
        </button>
        <button type="button" className="About-mobile-nav-link" onClick={() => handleMobileNavClick('/Login')}>
          Join as Photographer
        </button>
        {!logged_in && !authLoading && (
          <button type="button" className="About-mobile-login-btn" onClick={() => handleMobileNavClick('/login')}>
            Log In
          </button>
        )}
      </div>

      {/* Hero Section */}
      <section className="About-hero-section">
        <div className="About-hero-content">
          <div className="About-hero-badge">About LitStudio</div>
          <h1 className="About-hero-title">
            Professional photography,<br />
            <span className="About-hero-title-muted">without the guesswork.</span>
          </h1>
          <p className="About-hero-subtitle">
            <b>LitStudio</b> is a photography marketplace making professional photography accessible,
            affordable and trustworthy — connecting clients with verified photographers for
            weddings, events, portraits and brand shoots through one transparent platform.
          </p>
          <div className="About-hero-buttons">
            <button type="button" className="About-cta-button" onClick={() => navigate("/BrowsePhotographer")}>
              Browse Photographers
            </button>
            <button type="button" className="About-cta-secondary" onClick={() => navigate("/Login")}>
              Join as Photographer
            </button>
          </div>
        </div>
      </section>

      {/* Mission & Story */}
      <section className="About-story-section">
        <div className="About-story-inner">
          <div>
            <span className="About-eyebrow">Our mission</span>
            <p className="About-mission-statement">
              To change how memories are captured and preserved — by closing the gap between
              talented photographers and the people looking for them, regardless of budget
              or location.
            </p>
          </div>

          <div className="About-story-timeline">
            <div className="About-timeline-item">
              <div className="About-timeline-dot"></div>
              <div className="About-timeline-content">
                <div className="About-timeline-year">2024 — Ideation</div>
                <h4>The problem we kept seeing</h4>
                <p>Beautiful moments get lost in smartphone galleries, while talented photographers struggle to reach clients beyond their own locality.</p>
              </div>
            </div>
            <div className="About-timeline-item">
              <div className="About-timeline-dot"></div>
              <div className="About-timeline-content">
                <div className="About-timeline-year">2025 — Launch &amp; growth</div>
                <h4>From MVP to real bookings</h4>
                <p>With support from the AMRUT-MSSU Entrepreneurship Scheme, we built our MVP, onboarded photographers, and began helping families, young adults and brands find the right photographer.</p>
              </div>
            </div>
            <div className="About-timeline-item">
              <div className="About-timeline-dot"></div>
              <div className="About-timeline-content">
                <div className="About-timeline-year">Next — Scaling up</div>
                <h4>More cities, better matching</h4>
                <p>We're expanding to more cities and improving how we match clients to photographers, so booking a shoot is as simple as booking a ride.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reach section hidden — source data was a single Aug-Sep 2025
          snapshot and has gone stale. Flip to `true` below once fresher
          figures exist. A plain {/* *\/} comment can't wrap this block
          because it contains its own internal JSX comments, and JS
          comments don't nest — this caused a real bug previously
          (a stray "*\/}" rendered as literal visible text on the page).
          Reach — composition of our social content and audience.
          Source: CloutNine monthly report, 24 Aug - 23 Sep 2025.
          Percentages are reported figures; nothing here is interpolated. */}
      {false && (
      <section className="About-reach-section">
        <div className="About-reach-inner">
          <div className="About-section-head">
            <span className="About-eyebrow">Reach</span>
            <h2 className="About-section-title">How people find our work</h2>
            <p className="About-section-lede">
              A breakdown of how our social content performs and who it reaches.
            </p>
          </div>

          <div className="About-reach-grid">
            {/* Content mix — donut */}
            <div className="About-chart-card">
              <div className="About-chart-title">Content mix</div>
              <div className="About-chart-sub">Share of total views by format</div>

              <div className="About-donut-row">
                <div className="About-donut">
                  <svg width="160" height="160" viewBox="0 0 160 160" role="img"
                       aria-label="Content mix by share of views: Reels 51.6 percent, Posts 26.6 percent, Stories 21.8 percent">
                    <circle cx="80" cy="80" r="70" fill="none" stroke="#f4f4f5" strokeWidth="18" />
                    <circle className="About-donut-seg" cx="80" cy="80" r="70" stroke="#e8484e"
                            strokeDasharray="224.95 439.82" strokeDashoffset="0" strokeLinecap="butt">
                      <title>Reels — 51.6% of views</title>
                    </circle>
                    <circle className="About-donut-seg" cx="80" cy="80" r="70" stroke="#e8a33d"
                            strokeDasharray="114.99 439.82" strokeDashoffset="-226.95" strokeLinecap="butt">
                      <title>Posts — 26.6% of views</title>
                    </circle>
                    <circle className="About-donut-seg" cx="80" cy="80" r="70" stroke="#3f86bf"
                            strokeDasharray="93.88 439.82" strokeDashoffset="-343.94" strokeLinecap="butt">
                      <title>Stories — 21.8% of views</title>
                    </circle>
                    <text className="About-donut-center-value" x="80" y="76"
                          textAnchor="middle" transform="rotate(90 80 80)">51.6%</text>
                    <text className="About-donut-center" x="80" y="94"
                          textAnchor="middle" transform="rotate(90 80 80)">Reels</text>
                  </svg>
                </div>

                <div className="About-legend">
                  <div className="About-legend-row">
                    <span className="About-legend-swatch" style={{ background: '#e8484e' }}></span>
                    <span className="About-legend-label">Reels</span>
                    <span className="About-legend-value">51.6%</span>
                  </div>
                  <div className="About-legend-row">
                    <span className="About-legend-swatch" style={{ background: '#e8a33d' }}></span>
                    <span className="About-legend-label">Posts</span>
                    <span className="About-legend-value">26.6%</span>
                  </div>
                  <div className="About-legend-row">
                    <span className="About-legend-swatch" style={{ background: '#3f86bf' }}></span>
                    <span className="About-legend-label">Stories</span>
                    <span className="About-legend-value">21.8%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Audience split — bar */}
            <div className="About-chart-card">
              <div className="About-chart-title">Who we reach</div>
              <div className="About-chart-sub">Share of views by audience type</div>

              <div className="About-splitbar" role="img"
                   aria-label="Audience split: followers 58.8 percent, non-followers 41.2 percent of views">
                <div className="About-splitbar-seg" style={{ width: '58.8%', background: '#e8484e' }}>
                  58.8%
                  <span className="About-sr-only"> followers</span>
                </div>
                <div className="About-splitbar-seg" style={{ width: '41.2%', background: '#3f86bf' }}>
                  41.2%
                  <span className="About-sr-only"> non-followers</span>
                </div>
              </div>

              <div className="About-legend">
                <div className="About-legend-row">
                  <span className="About-legend-swatch" style={{ background: '#e8484e' }}></span>
                  <span className="About-legend-label">Followers</span>
                  <span className="About-legend-value">58.8%</span>
                </div>
                <div className="About-legend-row">
                  <span className="About-legend-swatch" style={{ background: '#3f86bf' }}></span>
                  <span className="About-legend-label">Discovered us organically</span>
                  <span className="About-legend-value">41.2%</span>
                </div>
              </div>
            </div>
          </div>

          <p className="About-reach-note">
            Social content performance, 24 Aug – 23 Sep 2025.
          </p>

          {/* Table view — same figures, for screen readers and any
              reader who cannot separate the segment colours. */}
          <table className="About-sr-only">
            <caption>Social content performance, 24 August to 23 September 2025</caption>
            <thead>
              <tr><th scope="col">Segment</th><th scope="col">Share of total views</th></tr>
            </thead>
            <tbody>
              <tr><td>Reels</td><td>51.6%</td></tr>
              <tr><td>Posts</td><td>26.6%</td></tr>
              <tr><td>Stories</td><td>21.8%</td></tr>
              <tr><td>Followers</td><td>58.8%</td></tr>
              <tr><td>Non-followers</td><td>41.2%</td></tr>
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* What Makes LitStudio Different */}
      <section className="About-difference-section">
        <div className="About-section-head">
          <span className="About-eyebrow">Why LitStudio</span>
          <h2 className="About-section-title">What makes us different</h2>
          <p className="About-section-lede">
            We do one thing, and we build the whole experience around it.
          </p>
        </div>

        <div className="About-difference-grid">
          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
            </div>
            <h3>Photography only</h3>
            <p>100% focused on photography, unlike generic service marketplaces that treat it as one category among hundreds.</p>
          </div>

          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <h3>Verified talent</h3>
            <p>Every photographer is verified and rated before their portfolio goes live, so there is no guesswork about who turns up.</p>
          </div>

          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <h3>Transparent pricing</h3>
            <p>Compare portfolios and packages upfront. Prices include taxes and charges, with no hidden fees at the end.</p>
          </div>

          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
            </div>
            <h3>Secure booking</h3>
            <p>Payment is held securely until the shoot is delivered, with clear timelines agreed before anyone picks up a camera.</p>
          </div>

          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3>Reach for photographers</h3>
            <p>Grow beyond word-of-mouth. Showcase your portfolio, set your own packages and reach clients you would never meet otherwise.</p>
          </div>

          <div className="About-difference-item">
            <div className="About-difference-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h3>Booked in minutes</h3>
            <p>Share your brief once, compare matches, and confirm — instead of chasing quotes across Instagram and WhatsApp.</p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="About-values-section">
        <div className="About-section-head">
          <span className="About-eyebrow">Our values</span>
          <h2 className="About-section-title">What we hold ourselves to</h2>
        </div>

        <div className="About-values-grid">
          <div className="About-value-card">
            <div className="About-value-index">01</div>
            <h3>Innovation</h3>
            <p>We keep pushing on the product so booking a photographer gets simpler every release, not more complicated.</p>
          </div>
          <div className="About-value-card">
            <div className="About-value-index">02</div>
            <h3>Quality</h3>
            <p>Excellence in every detail — from the booking experience to the final delivered memories.</p>
          </div>
          <div className="About-value-card">
            <div className="About-value-index">03</div>
            <h3>Collaboration</h3>
            <p>We are building a community where photographers and clients both come out ahead, not one at the other's expense.</p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="About-cta-section">
        <div className="About-cta-content">
          <h2>Ready to book your shoot?</h2>
          <p>
            Tell us what you need and we'll match you with photographers who fit your
            style, date and budget.
          </p>
          <button type="button" className="About-cta-button" onClick={() => navigate("/EnquiryForm")}>
            Book Now
          </button>
        </div>
      </section>

       {/* Footer */}
      <footer className="landing-footer">
  <div className="landing-footer-content">
    <div className="landing-footer-text">
      <p>Whether it's your big day, a milestone, or just because - we bring the moment, we'll make it Lit!</p>
    </div>
    <div className="landing-subscription">
      <p>Subscribe and be first to get offers</p>
      <form className="landing-subscription-form" onSubmit={handleSubscribe}>
        <input 
          type="email" 
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" className="landing-subscribe-btn">Subscribe!</button>
      </form>
    </div>
  </div>
  
  <div className="landing-social-links">
    <button type="button" className="landing-social-link" onClick={() => window.open('https://facebook.com', '_blank')}>
      <span>f</span>
    </button>
    <button type="button" className="landing-social-link" onClick={() => window.open('https://www.linkedin.com/company/thelitstudio/', '_blank')}>
      <span>in</span>
    </button>
    <button type="button" className="landing-social-link" onClick={() => window.open('https://www.instagram.com/litstudio.in/', '_blank')}>
      <span>ig</span>
    </button>
  </div>
  
  <div className="landing-footer-bottom">
    <p>&copy; 2025 LitStudio. All rights reserved.</p>
    <div className="landing-footer-links">
     <button type="button" onClick={() => setShowPrivacy(true)}>Privacy</button>

      <button type="button" onClick={() => setShowCookie(true)}>Cookie Settings</button>
      <button type="button" onClick={() => setShowTerms(true)}>Terms & Conditions</button>
    </div>
  </div>

  {/* Privacy Policy Modal */}
  <Modal 
    isOpen={showPrivacy} 
    onClose={() => setShowPrivacy(false)}
    title="Privacy Policy"
  >
    <p><strong>Effective Date:</strong> November 22, 2025</p>
    <p><strong>Last Updated:</strong> November 22, 2025</p>

    <h3>1. Introduction</h3>
    <p>
      Welcome to LitStudio ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our photography marketplace services.
    </p>

    <h3>2. Information We Collect</h3>
    
    <h4>2.1 Personal Information</h4>
    <p>We collect personal information that you voluntarily provide to us when you:</p>
    <ul>
      <li>Register on the platform as a user or photographer</li>
      <li>Make a booking or enquiry</li>
      <li>Subscribe to our newsletter</li>
      <li>Contact us for support</li>
    </ul>
    <p>This information may include:</p>
    <ul>
      <li>Full name and contact details (email address, phone number)</li>
      <li>Event details (type, date, location, requirements)</li>
      <li>Payment information (processed securely through third-party providers)</li>
      <li>Profile information and photographs (for photographers)</li>
      <li>User preferences and booking history</li>
    </ul>

    <h4>2.2 Automatically Collected Information</h4>
    <p>When you visit our website, we automatically collect certain information about your device, including:</p>
    <ul>
      <li>IP address and browser type</li>
      <li>Operating system and device information</li>
      <li>Usage data and browsing patterns</li>
      <li>Cookies and similar tracking technologies</li>
    </ul>

    <h3>3. How We Use Your Information</h3>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Provide, operate, and maintain our photography marketplace services</li>
      <li>Process bookings and facilitate connections between clients and photographers</li>
      <li>Send you booking confirmations, updates, and customer support communications</li>
      <li>Improve our website and personalize your experience</li>
      <li>Send promotional emails and newsletters (with your consent)</li>
      <li>Detect, prevent, and address technical issues or fraudulent activity</li>
      <li>Comply with legal obligations and enforce our Terms & Conditions</li>
    </ul>

    <h3>4. Information Sharing and Disclosure</h3>
    <p>We do not sell your personal information. We may share your information with:</p>
    <ul>
      <li><strong>Photographers:</strong> When you make a booking, we share necessary details with the photographer</li>
      <li><strong>Service Providers:</strong> Third-party vendors who assist us (payment processors, email services, analytics)</li>
      <li><strong>Legal Compliance:</strong> When required by law or to protect our rights</li>
      <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
    </ul>

    <h3>5. Data Security</h3>
    <p>
      We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
    </p>

    <h3>6. Your Rights and Choices</h3>
    <p>You have the right to:</p>
    <ul>
      <li>Access, update, or delete your personal information</li>
      <li>Opt-out of marketing communications</li>
      <li>Disable cookies through your browser settings</li>
      <li>Request a copy of your data</li>
      <li>Withdraw consent for data processing</li>
    </ul>

    <h3>7. Data Retention</h3>
    <p>
      We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
    </p>

    <h3>8. Children's Privacy</h3>
    <p>
      Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
    </p>

    <h3>9. Third-Party Links</h3>
    <p>
      Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
    </p>

    <h3>10. Changes to This Privacy Policy</h3>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
    </p>

    <h3>11. Contact Us</h3>
    <p>If you have questions about this Privacy Policy, please contact us:</p>
    <ul>
      <li><strong>Email:</strong>support@litstudio.in</li>
      <li><strong>Address:</strong> LitStudio, India</li>
    </ul>
  </Modal>

  {/* Cookie Policy Modal */}
  <Modal
    isOpen={showCookie}
    onClose={() => setShowCookie(false)}
    title="Cookie Policy"
  >
    <p><strong>Effective Date:</strong> November 22, 2025</p>
    <p><strong>Last Updated:</strong> November 22, 2025</p>

    <h3>1. What Are Cookies</h3>
    <p>
      Cookies are small text files stored on your device when you visit a website. They help the site remember information about your visit, such as your login session, so you don't have to re-enter it every time.
    </p>

    <h3>2. How We Use Cookies</h3>
    <p>
      LitStudio uses cookies for essential purposes only — primarily to keep you signed in and to remember your session while you browse or manage a booking. We do not currently use cookies for advertising, and we do not sell or share cookie data with third-party advertisers.
    </p>

    <h3>3. Types of Cookies We Use</h3>
    <ul>
      <li><strong>Strictly Necessary:</strong> Required for core functionality such as authentication and keeping you logged in. The site cannot function properly without these.</li>
      <li><strong>Preference Cookies:</strong> Remember choices you've made, such as your account type, so you don't have to set them again on each visit.</li>
    </ul>

    <h3>4. Managing Cookies</h3>
    <p>
      Most browsers let you view, manage, and delete cookies through their settings. Blocking strictly necessary cookies may prevent you from logging in or using core features of the platform.
    </p>

    <h3>5. Changes to This Policy</h3>
    <p>
      We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date.
    </p>

    <h3>6. Contact Us</h3>
    <p>If you have questions about this Cookie Policy, please contact us:</p>
    <ul>
      <li><strong>Email:</strong> support@litstudio.in</li>
      <li><strong>Address:</strong> LitStudio, India</li>
    </ul>
  </Modal>

  {/* Terms & Conditions Modal */}
  <Modal
  isOpen={showTerms}
  onClose={() => setShowTerms(false)}
  title="Terms & Conditions"
>
  <p><strong>Effective Date:</strong> November 22, 2025</p>
  <p><strong>Last Updated:</strong> November 22, 2025</p>

  <p>
    Welcome to LitStudio.in ("LitStudio", "we", "our", "us"). 
    LitStudio Technologies Private Limited operates this platform to connect Users with professional Photographers 
    for various types of shoots including weddings, events, personal portraits, food, fashion, and commercial photography.
  </p>
  <p>
    By using LitStudio, you ("User" or "Photographer") agree to comply with the following Terms & Conditions. 
    Please read them carefully before registering or making a booking.
  </p>

  <h3>1. Definitions</h3>
  <ul>
    <li><strong>Platform:</strong> The LitStudio website, mobile application, and associated services.</li>
    <li><strong>User/Client:</strong> Any individual or business using LitStudio to discover, compare, and book photographers.</li>
    <li><strong>Photographer:</strong> Any individual or business offering photography services through LitStudio.</li>
    <li><strong>Booking:</strong> A confirmed engagement between a User and a Photographer through LitStudio.</li>
    <li><strong>Content:</strong> All photographs, videos, reviews, text, logos, graphics, and other materials uploaded to the platform.</li>
    <li><strong>Intellectual Property (IP):</strong> Creative work such as photos, videos, and portfolios owned by the respective creators.</li>
  </ul>

  <h3>2. Acceptance of Terms</h3>
  <ul>
    <li>By registering or using LitStudio, you agree to these Terms, our Privacy Policy, and any future updates.</li>
    <li>You must be at least 18 years old and legally capable of entering into binding contracts.</li>
    <li>If you disagree with any part of these Terms, you must not use the platform.</li>
  </ul>

  <h3>3. Nature of Service</h3>
  <p><strong>LitStudio is a facilitator, not the service provider.</strong></p>
  <ul>
    <li>We provide a curated digital marketplace that helps Users connect with verified Photographers.</li>
    <li>All photography services are independently provided by photographers; LitStudio does not control the creative process or guarantee results.</li>
  </ul>

  <h3>4. User Terms</h3>
  
  <h4>4.1 Registration and Account</h4>
  <ul>
    <li>Users must create an account with valid contact details.</li>
    <li>Users are responsible for maintaining confidentiality of login credentials.</li>
    <li>Any suspicious activity must be immediately reported to <a href="mailto:support@litstudio.in">support@litstudio.in</a>.</li>
  </ul>

  <h4>4.2 Booking Process</h4>
  <ul>
    <li>Users can browse, select, and book photographers through the platform.</li>
    <li>Bookings are confirmed only upon successful payment through LitStudio's payment gateway.</li>
    <li>Users must provide accurate event details (date, location, duration, type, and preferences).</li>
  </ul>

  <h4>4.3 Payments and Fees</h4>
  <ul>
    <li>All payments are processed securely through LitStudio.</li>
    <li>A 1% convenience fee may apply to each booking.</li>
    <li>LitStudio holds the payment in escrow until the shoot is completed.</li>
    <li>Direct payments or offline transactions outside the platform are strictly prohibited.</li>
  </ul>

  <h4>4.4 Cancellations and Refunds</h4>
  <p><strong>Cancellation by User:</strong></p>
  <ul>
    <li>More than 7 days before the event → 80% refund</li>
    <li>Within 7 days → 50% refund</li>
    <li>Within 48 hours → No refund</li>
  </ul>
  <p><strong>Cancellation by Photographer:</strong></p>
  <ul>
    <li>Full 100% refund or replacement photographer will be provided.</li>
    <li>Refunds are processed within 7–10 business days.</li>
  </ul>

  <h4>4.5 User Conduct</h4>
  <ul>
    <li>Users must treat photographers with respect and professionalism.</li>
    <li>Harassment, abuse, or inappropriate behavior will lead to suspension or permanent ban.</li>
    <li>Users must not request or encourage photographers to bypass LitStudio for future bookings.</li>
  </ul>

  <h3>5. Photographer Terms</h3>
  
  <h4>5.1 Eligibility and Verification</h4>
  <ul>
    <li>Photographers must provide valid government-issued ID and proof of original portfolio work.</li>
    <li>All portfolios must feature the photographer's own authentic work — not copied, downloaded, or re-edited content from others.</li>
  </ul>

  <h4>5.2 Prohibition on Copying or Plagiarism</h4>
  <p>Photographers are <strong>strictly prohibited</strong> from:</p>
  <ul>
    <li>Uploading or claiming credit for others' photos or videos.</li>
    <li>Downloading, editing, or reposting work that belongs to another photographer.</li>
    <li>Using AI-generated or stock images without disclosure.</li>
  </ul>
  <p>If a photographer is found using copied or stolen work:</p>
  <ul>
    <li>The account will be permanently suspended.</li>
    <li>Payments may be withheld.</li>
    <li>Legal action may be initiated under the Copyright Act, 1957 (India).</li>
  </ul>

  <h4>5.3 Listings and Pricing</h4>
  <ul>
    <li>Photographers must accurately list services, packages, and sample work.</li>
    <li>Prices displayed must include all taxes and service charges.</li>
    <li>Once a booking is confirmed, the photographer must honor the agreed rate.</li>
  </ul>

  <h4>5.4 Membership and Commission</h4>
  <ul>
    <li>Photographers may be required to pay a monthly membership fee (₹70–₹100) to stay listed.</li>
    <li>LitStudio will deduct a 10% commission on each completed booking.</li>
  </ul>

  <h4>5.5 Delivery Commitments</h4>
  <ul>
    <li>Deliverables (edited photos/videos) must be submitted within the timeline agreed with the User.</li>
    <li>Delays beyond 7 days of the committed delivery date without valid cause may result in penalties or payment withholding.</li>
  </ul>

  <h4>5.6 Conduct and Professionalism</h4>
  <ul>
    <li>Photographers must maintain respectful behavior during shoots.</li>
    <li>Any misconduct (harassment, unprofessionalism, negligence) may result in immediate delisting.</li>
    <li>Communication with clients must remain through the LitStudio platform until the booking is confirmed.</li>
  </ul>

  <h3>6. Intellectual Property (IP) Rights</h3>
  
  <h4>6.1 Ownership</h4>
  <ul>
    <li>Photographers retain full ownership of their creative work.</li>
    <li>By uploading content, photographers grant LitStudio a non-exclusive, royalty-free license to use such content for marketing, promotional, and platform purposes.</li>
  </ul>

  <h4>6.2 Content Integrity</h4>
  <p>Users and photographers must not:</p>
  <ul>
    <li>Copy, reuse, or distribute others' photos/videos.</li>
    <li>Edit, watermark, or modify any portfolio images without permission.</li>
    <li>Claim ownership or remove author credits.</li>
  </ul>

  <h4>6.3 Copyright Violations</h4>
  <ul>
    <li>LitStudio maintains a zero-tolerance policy for plagiarism or IP theft.</li>
    <li>If reported, the accused photographer's account will be suspended immediately pending investigation.</li>
    <li>Repeat offenders will face permanent bans and legal proceedings.</li>
  </ul>

  <h3>7. Ratings, Reviews, and Feedback</h3>
  <ul>
    <li>After every booking, both User and Photographer may submit ratings and reviews.</li>
    <li>Reviews must be fair, honest, and based on personal experience.</li>
    <li>LitStudio may remove fake, offensive, or misleading reviews.</li>
    <li>Consistently high ratings may increase a photographer's visibility and tier level (Normal, Pro, Premium).</li>
  </ul>

  <h3>8. Dispute Resolution</h3>
  <ul>
    <li>Both parties should first attempt to resolve disputes amicably.</li>
    <li>If unresolved, LitStudio may mediate using booking details, chat history, and proof of delivery.</li>
    <li>LitStudio's mediation decision is final and binding for payment-related issues.</li>
  </ul>

  <h3>9. Prohibited Activities</h3>
  <p>Users or Photographers must not:</p>
  <ul>
    <li>Conduct transactions outside the LitStudio platform.</li>
    <li>Share false information, fake portfolios, or manipulated reviews.</li>
    <li>Upload offensive, obscene, or copyrighted material without permission.</li>
    <li>Attempt to hack, scrape, or misuse the platform.</li>
    <li>Engage in defamation, abuse, or discrimination against any user.</li>
  </ul>

  <h3>10. Liability and Disclaimer</h3>
  <ul>
    <li>LitStudio is not responsible for any disputes, damages, or losses arising between Users and Photographers.</li>
    <li>We do not guarantee the quality, quantity, or creative output of any service.</li>
    <li>LitStudio's total liability in any case shall not exceed the service fee earned by LitStudio from that booking.</li>
  </ul>

  <h3>11. Privacy</h3>
  <ul>
    <li>Personal data is collected, stored, and processed in accordance with our Privacy Policy.</li>
    <li>We do not sell or share personal data without consent, except when required by law.</li>
  </ul>

  <h3>12. Termination</h3>
  <ul>
    <li>LitStudio reserves the right to suspend or terminate any account for violations of these Terms.</li>
    <li>Users and Photographers may request account deletion by contacting <a href="mailto:support@litstudio.in">support@litstudio.in</a>.</li>
  </ul>

  <h3>13. Amendments</h3>
  <ul>
    <li>LitStudio may modify these Terms at any time.</li>
    <li>Updated terms will be published on this page, and continued use implies acceptance.</li>
  </ul>

  <h3>14. Governing Law</h3>
  <ul>
    <li>These Terms are governed by the laws of India.</li>
    <li>Jurisdiction for all disputes shall be the courts of Mumbai, Maharashtra.</li>
  </ul>

  <h3>15. Contact Us</h3>
  <p>For any queries, support, or grievance redressal:</p>
  <ul>
    <li><strong>Email:</strong> <a href="mailto:support@litstudio.in">support@litstudio.in</a></li>
    <li><strong>Website:</strong> <a href="https://www.litstudio.in">www.litstudio.in</a></li>
  </ul>

  <p style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f3f4f6', borderRadius: '8px', fontStyle: 'italic', color: '#6b7280' }}>
    By using LitStudio, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
  </p>
</Modal>
</footer>
    </div>
  );
}

export default AboutUs;
